import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Clock, MapPin, Users, CheckCircle, XCircle, AlertCircle, Handshake, Briefcase, Coffee } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { Event, EventAssignment, EventType } from '@/types/index';
import { format, isAfter, isBefore } from 'date-fns';

interface EventWithAssignment extends Event {
  event_assignments: EventAssignment[];
}

interface EmployeeEventsTabProps {
  employeeId: string;
}

const EmployeeEventsTab: React.FC<EmployeeEventsTabProps> = ({ employeeId }) => {
  const [events, setEvents] = useState<EventWithAssignment[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          event_assignments!inner (
            *
          )
        `)
        .eq('event_assignments.employee_id', employeeId)
        .order('event_date', { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error("Failed to load your events. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [employeeId]);

  const handleAttendanceResponse = async (assignmentId: string, status: 'confirmed' | 'declined') => {
    try {
      const { error } = await supabase
        .from('event_assignments')
        .update({
          attendance_status: status,
          response_at: new Date().toISOString(),
        })
        .eq('id', assignmentId);

      if (error) throw error;

      toast.success("Your response has been recorded successfully!");
      
      // Refresh the events list to show updated status
      await fetchEvents();
    } catch (error) {
      console.error('Error updating attendance:', error);
      toast.error("Failed to update attendance response. Please try again.");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      case 'attended':
        return 'bg-blue-100 text-blue-800';
      case 'no_show':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    }
  };

  const getEventTypeColor = (eventType: EventType) => {
    switch (eventType) {
      case 'customer':
        return 'bg-green-100 text-green-800';
      case 'employee_voluntary':
        return 'bg-blue-100 text-blue-800';
      case 'employee_mandatory':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getEventTypeLabel = (eventType: EventType) => {
    switch (eventType) {
      case 'employee_voluntary':
        return 'Voluntary';
      case 'employee_mandatory':
        return 'Mandatory';
      default:
        return 'Event';
    }
  };

  const getEventTypeIcon = (eventType: EventType) => {
    switch (eventType) {
      case 'employee_voluntary':
        return <Handshake className="h-4 w-4" />;
      case 'employee_mandatory':
        return <Briefcase className="h-4 w-4" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  const canRespondToEvent = (eventDate: string, assignment: EventAssignment) => {
    const eventDateTime = new Date(eventDate);
    const now = new Date();
    
    // Can respond if event is in the future and status is pending
    return isAfter(eventDateTime, now) && assignment.attendance_status === 'pending';
  };

  const isEventPast = (eventDate: string) => {
    const eventDateTime = new Date(eventDate);
    const now = new Date();
    return isBefore(eventDateTime, now);
  };

  const categorizeEvents = (events: EventWithAssignment[]) => {
    const upcoming = events.filter(event => !isEventPast(event.event_date));
    const past = events.filter(event => isEventPast(event.event_date));
    
    const voluntary = events.filter(event => event.event_type === 'employee_voluntary');
    const mandatory = events.filter(event => event.event_type === 'employee_mandatory');
    
    return { upcoming, past, voluntary, mandatory };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const { upcoming, past, voluntary, mandatory } = categorizeEvents(events);

  const renderEventCard = (event: EventWithAssignment) => {
    const assignment = event.event_assignments[0];
    const canRespond = canRespondToEvent(event.event_date, assignment);
    const isPast = isEventPast(event.event_date);
    
    return (
      <Card key={event.id} className={`overflow-hidden ${isPast ? 'opacity-75' : ''}`}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <div className="flex justify-between items-start w-full">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {getEventTypeIcon(event.event_type)}
                <CardTitle className="text-lg font-medium">{event.name}</CardTitle>
                <Badge className={getEventTypeColor(event.event_type)}>
                  {getEventTypeLabel(event.event_type)}
                </Badge>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {format(new Date(event.event_date), 'PPP')}
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {event.start_time} ({event.duration_minutes} min)
                </div>
                {event.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {event.location}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={getStatusColor(assignment.attendance_status)}>
                {assignment.attendance_status.replace('_', ' ')}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {event.description && (
            <p className="text-gray-700 mb-4">{event.description}</p>
          )}
          
          {event.event_type === 'employee_voluntary' && canRespond && (
            <div className="flex gap-2 mt-4">
              <Button
                size="sm"
                onClick={() => handleAttendanceResponse(assignment.id, 'confirmed')}
                className="flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Confirm Attendance
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAttendanceResponse(assignment.id, 'declined')}
                className="flex items-center gap-2"
              >
                <XCircle className="h-4 w-4" />
                Decline
              </Button>
            </div>
          )}
          
          {event.event_type === 'employee_mandatory' && (
            <div className="mt-4 p-3 bg-orange-50 rounded-md">
              <p className="text-sm text-orange-800">
                <strong>Mandatory Event:</strong> Your attendance is required for this event.
              </p>
            </div>
          )}
          
          {assignment.response_at && (
            <div className="mt-4 text-sm text-gray-600">
              Response recorded on: {format(new Date(assignment.response_at), 'PPp')}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">My Events</h2>
          <p className="text-gray-600">View your assigned events and respond to invitations</p>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant="outline">
            {upcoming.length} upcoming
          </Badge>
          <Badge variant="outline">
            {past.length} past
          </Badge>
        </div>
      </div>

      {events.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold mb-2">No events assigned</h3>
            <p className="text-gray-600">You don't have any upcoming events.</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="upcoming" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="upcoming" className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Upcoming ({upcoming.length})
            </TabsTrigger>
            <TabsTrigger value="voluntary" className="flex items-center gap-2">
              <Handshake className="h-4 w-4" />
              Voluntary ({voluntary.length})
            </TabsTrigger>
            <TabsTrigger value="mandatory" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Mandatory ({mandatory.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Past ({past.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="mt-6">
            {upcoming.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-semibold mb-2">No upcoming events</h3>
                  <p className="text-gray-600">All caught up! No events scheduled for the near future.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {upcoming.map(renderEventCard)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="voluntary" className="mt-6">
            {voluntary.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Handshake className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-semibold mb-2">No voluntary events</h3>
                  <p className="text-gray-600">No team building or optional events assigned to you.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {voluntary.map(renderEventCard)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="mandatory" className="mt-6">
            {mandatory.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-semibold mb-2">No mandatory events</h3>
                  <p className="text-gray-600">No required meetings or training sessions assigned to you.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {mandatory.map(renderEventCard)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past" className="mt-6">
            {past.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-semibold mb-2">No past events</h3>
                  <p className="text-gray-600">No event history to display yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {past.map(renderEventCard)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default EmployeeEventsTab;